import React, { FC, useCallback, useState } from 'react';
import { createUseStyles } from 'src/theming';
import EntityTable from 'src/components/Table';
import { TableColumn } from 'src/components/Table/EntityTable';
import interventionsState, { InterventionState } from 'src/recoil/atoms/ticketInterventions';
import Intervention from 'src/interfaces/api/Intervention';
import { CollectionResponse } from 'src/interfaces/api/api';
import { Order } from 'src/interfaces/Table';
import EmployeeCell from './components/EmployeeCell';
import { ellipsis } from 'src/utils/StringUtils';
import moment from 'moment';
import ConfirmationDialog from 'src/components/ConfirmationDialog';
import { MdTimer as TimerIcon } from '@react-icons/all-files/md/MdTimer';
import { BiBuildingHouse as OfficeIcon } from '@react-icons/all-files/bi/BiBuildingHouse';
import { MdDelete as DeleteIcon } from '@react-icons/all-files/md/MdDelete';
import { IconButton } from '@material-ui/core';

const useStyles = createUseStyles({
  root: {
    padding: 16,
  },
});

interface Props {
  onRemoveIntervention: (id: Intervention['id']) => Promise<any>;
  onGetInterventions: (
    page: number,
    limit: number,
    orderBy: keyof InterventionState,
    order: Order
  ) => Promise<CollectionResponse<InterventionState>>;
}

const columns: TableColumn<Intervention>[] = [
  {
    id: 'employeeId',
    type: 'text',
    label: 'Employé',
    renderValue: ({ employeeId }) => <EmployeeCell employeeId={employeeId} />,
  },
  {
    id: 'description',
    type: 'text',
    label: 'Description',
    renderValue: ({ description, onSite }) => (
      <div style={{ display: 'flex', alignItems: 'center' }} title={description}>
        {onSite && <OfficeIcon title='intervention sur site' size={18} />}
        {ellipsis(description, 90)}
      </div>
    ),
  },
  {
    id: 'startDate',
    type: 'datetime',
    label: 'Début',
    sortable: true,
  },
  {
    id: 'endDate',
    type: 'datetime',
    label: 'Fin',
    sortable: true,
  },
  {
    id: 'duration',
    type: 'text',
    label: 'Durée (min.)',
    renderValue: ({ endDate, startDate }) => (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {moment(endDate).diff(moment(startDate), 'minutes')} <TimerIcon style={{ marginLeft: 5 }} />
      </div>
    ),
  },
];

const InterventionsCard: FC<Props> = ({ onRemoveIntervention, onGetInterventions }) => {
  const [interventionToRemove, setInterventionToRemove] = useState<Intervention['id']>();
  const classes = useStyles();

  const handleRemoveIntervention = async () => {
    if (interventionToRemove) {
      await onRemoveIntervention(interventionToRemove);
      setInterventionToRemove(undefined);
    }
  };

  const handleGetInterventions = useCallback(
    async (page: number, limit: number, orderBy: keyof InterventionState, order: Order) => {
      return await onGetInterventions(page, limit, orderBy, order);
    },
    [onGetInterventions]
  );

  return (
    <div className={classes.root}>
      <EntityTable
        columns={columns}
        defaultOrderBy='startDate'
        atomState={interventionsState}
        fetchEntitiesCallback={handleGetInterventions}
        actions={(intervention) => (
          <IconButton size='small' onClick={() => setInterventionToRemove(intervention.id)}>
            <DeleteIcon />
          </IconButton>
        )}
      />

      <ConfirmationDialog
        title='Attention'
        confirmationText='Supprimer'
        description='Vous allez supprimer cette intervention. Êtes-vous sûr ?'
        onClose={() => setInterventionToRemove(undefined)}
        open={!!interventionToRemove}
        onValidate={handleRemoveIntervention}
      />
    </div>
  );
};

export default InterventionsCard;
